import React, { useCallback, useMemo, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { SivaContractCreateDto, SivaPhase } from 'src/api/v2';
import ValueField from 'src/components/ValueField/ValueField';
import { useUsers } from 'src/features/users';
import { useExternalCompany } from 'src/hooks/use-external-company';
import { getMonthAndYearString } from 'src/utils/date';
import { useCalculateSivaKTUDates } from 'src/hooks/use-siva-ktu-date-calculation';
import { bransjeOptions, ideaSourceOptions } from '../../constants';
import { emailRegex } from 'src/utils/regex';
import { useTranslation } from 'react-i18next';
import useAdvisorOptions from '../../hooks/use-advisor-options';
import { Link } from 'react-router-dom';
import { useAuth } from 'src/features/authorization';
import useGeonorge from 'src/features/one-way-in/hooks/use-geonorge';

import { useOpenReportingYears } from '../../hooks/use-siva-open-reporting-years';
import { useCluster } from 'src/features/cluster';
import {
  Alert,
  Select,
  Textfield,
  Field,
  Label,
  ValidationMessage,
  Heading,
  Suggestion,
  Tag,
  Button,
  Icon,
} from '@innovationnorway/cl-react';
import CompanyBaseInfoBox from './CompanyBaseInfoBox';

type Props = {
  organizationNumber: string;
  form: UseFormReturn<SivaContractCreateDto>;
  renderPhase?: boolean;
};

const ideaNameMaxCharacters = 100;
const NewCompanyContract: React.FC<Props> = ({ organizationNumber, form, renderPhase = false }) => {
  const { checkAccess } = useAuth();

  const sivaCoreAccessCheck = checkAccess('SivaCore').write;
  const isBusinessGarden = checkAccess('UI_SivaBusinessGarden').read;

  const { t: tCommon } = useTranslation();
  const { t: tSiva } = useTranslation('siva');

  const { isLoading: isLoadingUsers } = useUsers();
  const { cluster } = useCluster();

  const { data: company, isInitialLoading: isLoadingCompany } = useExternalCompany(organizationNumber);

  // Dette gjør at vi kan refetch rådgivere i et intervall, etter at bruker har trykt på linken for Tilgangsstyring
  const [refetchInterval, setRefetchInterval] = useState(0);
  const REFETCH_INTERVAL = 1000 * 15;
  const startRefetchInterval = useCallback(() => setRefetchInterval(REFETCH_INTERVAL), [REFETCH_INTERVAL]);
  const { municipalities, countiesWithMunicipalities, getMunicipalityNameByNumber } = useGeonorge();

  const duplicateMunicipalityNames = useMemo(() => {
    const counts = new Map<string, number>();
    municipalities.forEach((m) => counts.set(m.name, (counts.get(m.name) ?? 0) + 1));
    return new Set(
      Array.from(counts.entries())
        .filter(([, c]) => c > 1)
        .map(([name]) => name),
    );
  }, [municipalities]);

  const getMunicipalityLabel = useCallback(
    (number: string | undefined, name: string) => {
      if (duplicateMunicipalityNames.has(name)) {
        const countyName = countiesWithMunicipalities.find((county) =>
          county.kommuner.some((k) => k.kommunenummer === number),
        )?.fylkesnavn;
        return countyName ? `${name} (${countyName})` : name;
      }
      return name;
    },
    [duplicateMunicipalityNames, countiesWithMunicipalities],
  );
  const { advisorOptions } = useAdvisorOptions({
    refetchInterval,
  });
  const { validateMinDate, validateMaxDate } = useOpenReportingYears(cluster?.id);

  const isLoading = isLoadingUsers || isLoadingCompany;

  const { control, watch, setValue, getValues } = form;

  React.useEffect(() => {
    if (isBusinessGarden && sivaCoreAccessCheck) {
      setValue('sivaPhase', SivaPhase.Targeted);
    }
  }, [isBusinessGarden, sivaCoreAccessCheck, setValue]);

  const fromDateWatch = watch('fromDate');
  const [startupDate, midDate] = useCalculateSivaKTUDates(fromDateWatch);

  const renderFormSendingDate = (date: Date | undefined) => {
    if (!date) {
      return tCommon('unknown');
    }

    return getMonthAndYearString(date);
  };

  if (isLoading) {
    return <p>{tCommon('loadingData')} ...</p>;
  }

  return (
    <div className="display--flex flex-direction--column gap--ds-9">
      <div className="display--flex flex-direction--column gap--ds-4">
        <CompanyBaseInfoBox company={company} />

        <Heading level={3} className="font-size--ds-6">
          {form.getValues('sivaPhase') === SivaPhase.Idea
            ? tSiva('idea.description')
            : tSiva('newCompany.contract.section.info.title')}
        </Heading>

        {sivaCoreAccessCheck && !isBusinessGarden && renderPhase && (
          <ValueField label={tCommon('phase')} value={tSiva(`phase.${getValues('sivaPhase')}`)} />
        )}

        {sivaCoreAccessCheck && !isBusinessGarden && (
          <Controller
            control={control}
            name="name"
            rules={{
              required: form.getValues('organizationNumber')
                ? undefined
                : tSiva('newCompany.contract.form.name.required'),
              maxLength: {
                value: ideaNameMaxCharacters,
                message: tSiva('newCompany.contract.form.ideaName.maxLength', {
                  value: ideaNameMaxCharacters,
                }),
              },
            }}
            render={({ field, fieldState }) => (
              <Textfield
                label={tSiva('newCompany.contract.form.name.label')}
                description={tSiva('newCompany.contract.form.name.description')}
                error={fieldState.error?.message}
                counter={ideaNameMaxCharacters}
                {...field}
              />
            )}
          />
        )}

        <Controller
          control={control}
          name="ideaSource"
          rules={{ required: tSiva('newCompany.contract.form.ideaSource.required') }}
          render={({ field, fieldState }) => (
            <Field>
              <Label>{`${tSiva('newCompany.contract.form.ideaSource.label')}`}</Label>
              <Select {...field} value={field.value || ''}>
                <Select.Option value="" disabled>
                  {tSiva('newCompany.contract.form.ideaSource.placeholder')}
                </Select.Option>
                {ideaSourceOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.text}
                  </Select.Option>
                ))}
              </Select>
              {fieldState.error?.message && (
                <ValidationMessage data-size="sm">{fieldState.error?.message}</ValidationMessage>
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="externalIndustry"
          rules={{
            required:
              form.watch('sivaPhase') === SivaPhase.Idea
                ? undefined
                : tSiva('newCompany.contract.form.externalIndustry.required'),
          }}
          render={({ field, fieldState }) => (
            <Field>
              <Label>{`${tSiva('newCompany.contract.form.externalIndustry.label')}`}</Label>
              <Select {...field} value={field.value || ''}>
                <Select.Option value="" disabled>
                  {tSiva('newCompany.contract.form.externalIndustry.placeholder')}
                </Select.Option>
                {bransjeOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.text}
                  </Select.Option>
                ))}
              </Select>
              {fieldState.error?.message && (
                <ValidationMessage data-size="sm">{fieldState.error?.message}</ValidationMessage>
              )}
            </Field>
          )}
        />

        <Controller
          control={control}
          name="fromDate"
          rules={{
            required: tSiva('newCompany.contract.form.fromDate.required'),
            validate: {
              sameYear: (value) => {
                if (!value) {
                  return true;
                }

                const date = new Date(value);
                if (date < validateMinDate || date > validateMaxDate) {
                  return tSiva('newCompany.contract.form.fromDate.sameYear');
                }

                return true;
              },
            },
          }}
          render={({ field: { value, ...field }, fieldState }) => (
            <Textfield
              type="date"
              label={
                form.getValues('sivaPhase') === SivaPhase.Idea
                  ? tSiva('newCompany.contract.form.registrationDate.label')
                  : tSiva('newCompany.contract.form.fromDate.label')
              }
              {...field}
              value={value ? new Date(value).toJSON().substring(0, 10) : undefined}
              error={fieldState.error?.message}
            />
          )}
        />

        {form.getValues('sivaPhase') !== SivaPhase.Idea && (
          <>
            <div>
              <Label>{tSiva('newCompany.contract.form.formStartupDate.label')}</Label>
              <span className="display--block margin-top--ds-2">{renderFormSendingDate(startupDate)}</span>
            </div>
            <div>
              <Label>{tSiva('newCompany.contract.form.formMidDate.label')}</Label>
              <span className="display--block margin-top--ds-2">{renderFormSendingDate(midDate)}</span>
            </div>
          </>
        )}
      </div>
      <div className="display--flex flex-direction--column gap--ds-4">
        <div>
          <Heading level={3} className="font-size--ds-6">
            {tSiva('newCompany.contract.section.contact.title')}
          </Heading>
          <span className="description">{tSiva('newIdeaForm.section.subTitle.contact')}</span>
        </div>

        <Controller
          control={control}
          name="contactFirstName"
          rules={{ required: tSiva('newCompany.contract.form.contactFirstName.required') }}
          render={({ field, fieldState }) => (
            <Textfield
              label={tSiva('newCompany.contract.form.contactFirstName.label')}
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="contactLastName"
          rules={{ required: tSiva('newCompany.contract.form.contactLastName.required') }}
          render={({ field, fieldState }) => (
            <Textfield
              label={tSiva('newCompany.contract.form.contactLastName.label')}
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="contactEmail"
          rules={{
            required: tSiva('newCompany.contract.form.contactEmail.required'),
            validate: {
              email: (value) => {
                if (!value) {
                  return true;
                }
                if (!emailRegex.test(value)) {
                  return tSiva('newCompany.contract.form.contactEmail.validateEmail');
                }
              },
            },
          }}
          render={({ field, fieldState }) => (
            <Textfield
              type="email"
              label={tSiva('newCompany.contract.form.contactEmail.label')}
              {...field}
              error={fieldState.error?.message}
            />
          )}
        />
        {!isBusinessGarden && form.getValues('sivaPhase') === SivaPhase.Idea && (
          <>
            {form.watch('municipality') === undefined ? (
              <Controller
                control={control}
                name="municipality"
                render={({ field }) => (
                  <Field>
                    <Label>{tSiva('newCompany.contract.form.municipality.label')}</Label>
                    <Suggestion name={field.name} onSelectedChange={(value) => field.onChange(value?.value)}>
                      <Suggestion.Input />
                      <Suggestion.List>
                        <Suggestion.Empty>{tCommon('noHits')}</Suggestion.Empty>
                        {municipalities
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((muni) => {
                            const label = getMunicipalityLabel(muni.number, muni.name);
                            return (
                              <Suggestion.Option key={muni.number} label={label} value={muni.number}>
                                {label}
                              </Suggestion.Option>
                            );
                          })}
                      </Suggestion.List>
                    </Suggestion>
                  </Field>
                )}
              />
            ) : (
              <div>
                <Label>{tSiva('newCompany.contract.form.municipality.label')}</Label>
                <div className="display--flex gap--1 align-items--center">
                  <span>
                    {(() => {
                      const number = form.watch('municipality');
                      const name = getMunicipalityNameByNumber(number);
                      return name ? getMunicipalityLabel(number, name) : null;
                    })()}
                  </span>
                  <Button
                    variant="tertiary"
                    data-size="sm"
                    aria-label={`${tCommon('edit')} ${tCommon('municipality').toLowerCase()}`}
                    onClick={() => form.setValue('municipality', undefined)}
                  >
                    <Icon prefix="fasr" iconName="pencil" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className="display--flex flex-direction--column gap--ds-4">
        <Heading level={3} className="font-size--ds-6">
          {tSiva('newCompany.contract.section.followUp.title')}
        </Heading>

        <Controller
          control={control}
          name="advisorId"
          rules={{
            required: form.getValues('advisorId')
              ? undefined
              : tSiva('newCompany.contract.form.advisorId.required'),
          }}
          render={({ field, fieldState }) => {
            const selectedAdvisor = advisorOptions.find((x) => x.value === field.value);

            return (
              <Field>
                <Label>{`${tCommon('advisor')}`}</Label>
                <Suggestion
                  name={field.name}
                  defaultSelected={
                    selectedAdvisor
                      ? { value: selectedAdvisor.value, label: selectedAdvisor.text }
                      : undefined
                  }
                  onSelectedChange={(value) => field.onChange(value?.value ?? '')}
                >
                  <Suggestion.Input />
                  <Suggestion.List>
                    <Suggestion.Empty>{tCommon('noHits')}</Suggestion.Empty>
                    {advisorOptions.map((advisor) => (
                      <Suggestion.Option key={advisor.value} label={advisor.text} value={advisor.value}>
                        {advisor.text}
                      </Suggestion.Option>
                    ))}
                  </Suggestion.List>
                </Suggestion>
                <p className="description">
                  {tSiva('newCompany.contract.form.advisorId.link.text')}{' '}
                  <Link
                    to={`/actor-profile/settings/access`}
                    target="_blank"
                    onClick={() => {
                      startRefetchInterval();
                    }}
                  >
                    <span>{tCommon('accessControl')}</span>
                  </Link>
                  .
                </p>
                {fieldState.error?.message && (
                  <ValidationMessage data-size="sm">{fieldState.error?.message}</ValidationMessage>
                )}
              </Field>
            );
          }}
        />

        <Controller
          control={control}
          name="notes"
          rules={{
            maxLength: {
              value: 1000,
              message: tSiva('newCompany.contract.form.notes.maxLength'),
            },
          }}
          render={({ field, fieldState }) => (
            <Textfield
              label={
                <div className="display--flex justify-content--space-between align-items--center">
                  <span>{tSiva('newCompany.contract.form.notes.label')}</span>
                  <Tag data-color={'accent' as const}>
                    {tCommon('optional').charAt(0).toUpperCase() + tCommon('optional').slice(1)}
                  </Tag>
                </div>
              }
              description={tSiva('newCompany.contract.form.notes.label.description')}
              multiline
              {...field}
              onChange={(e) => field.onChange(e.target.value || '')}
              error={fieldState.error?.message}
              counter={1000}
            />
          )}
        />
      </div>

      {!form.formState.isValid && form.formState.submitCount > 0 && (
        <Alert data-color="danger">{tSiva('newCompany.contract.form.errors')}</Alert>
      )}
    </div>
  );
};

export default NewCompanyContract;
